import { createContext, useContext, useState, ReactNode, useCallback, useEffect } from "react";
import { trpc, trpcVanilla } from "@/lib/trpc";
import { setSentryUser, clearSentryUser } from "@/lib/sentry";

interface User {
  id: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  role: string;
  companyId?: string | null;
  isStaff?: boolean;
  staffRole?: "SUPER_ADMIN" | "ADMIN_OPS" | "ADMIN_SUPPORT" | null;
}

interface AuthContextType {
  user: User | null;
  /** Kept for back-compat with components that displayed the token. Always null now. */
  token: null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/** Legacy keys from the localStorage-era — wiped on mount. */
const LEGACY_TOKEN_KEY = "admin_token";
const LEGACY_USER_KEY = "admin_user";

function hasCsrfCookie(): boolean {
  if (typeof document === "undefined") return false;
  return /(?:^|;\s*)constrigo_csrf=/.test(document.cookie);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  // Migrate away from localStorage: clear any leftover state.
  useEffect(() => {
    if (typeof window === "undefined") return;
    localStorage.removeItem(LEGACY_TOKEN_KEY);
    localStorage.removeItem(LEGACY_USER_KEY);
  }, []);

  const [loginHint, setLoginHint] = useState<boolean>(hasCsrfCookie);

  const meQuery = trpc.auth.me.useQuery(undefined, {
    enabled: loginHint,
    retry: false,
    onError: () => setLoginHint(false),
  });

  const loginMutation = trpc.auth.login.useMutation();

  const login = useCallback(async (email: string, password: string) => {
    try {
      const result = await loginMutation.mutateAsync({ email, password });

      // OPS is reserved for staff only.
      if (!result.user.isStaff || !result.user.staffRole) {
        throw new Error("Accès refusé. Seuls les membres du staff peuvent accéder à cette interface.");
      }

      // Cookies have been set by the server. Mark the client as logged in
      // and trigger /auth.me to hydrate the full profile.
      setLoginHint(true);
      void meQuery.refetch();
    } catch (error: any) {
      if (error?.message?.includes("Failed to fetch")) {
        throw new Error("Le serveur backend n'est pas accessible. Assurez-vous qu'il est démarré sur http://localhost:3001");
      }
      throw error;
    }
  }, [loginMutation, meQuery]);

  const logout = useCallback(async () => {
    try {
      await trpcVanilla.auth.logoutSelf.mutate(undefined as any);
    } catch {
      // Cookie might already be stale — ignore.
    }
    setLoginHint(false);
    if (typeof window !== "undefined") {
      localStorage.removeItem(LEGACY_TOKEN_KEY);
      localStorage.removeItem(LEGACY_USER_KEY);
    }
  }, []);

  const user = (loginHint ? (meQuery.data as User | undefined) : null) ?? null;

  // Keep Sentry aware of WHO is connected so every event/log names the user.
  useEffect(() => {
    if (user) {
      setSentryUser({
        id: user.id,
        email: user.email,
        role: user.staffRole ?? user.role,
        companyId: user.companyId ?? undefined,
      });
    } else {
      clearSentryUser();
    }
  }, [user]);

  const isLoading = loginHint && meQuery.isLoading;

  return (
    <AuthContext.Provider
      value={{
        user,
        token: null,
        login,
        logout,
        isAuthenticated: loginHint && !!user,
        isLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
